import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Autocomplete,
  CircularProgress,
  Typography,
  Box,
  Grid,
  Alert,
  OutlinedInput,
  Chip,
  Avatar,
  Checkbox,
  FormHelperText,
} from "@mui/material";
import {
  fetchCountries,
  loadAccountDropdown,
  createNewGroup,
} from "../../Action/api"; // UPDATED: Import new API
import { getAuthTokenPayload } from "../../Utils/index";

const AddNewGroup = ({
  open,
  onClose,
  onSubmit,
  editingGroup,
  isCloning, // ADDED
  groupTypes,
}) => {
  const userId = getAuthTokenPayload()?.userId ?? "";

  // Group Form States
  const [groupForm, setGroupForm] = useState({
    groupName: "",
    description: "",
    groupType: "",
    accounts: [],
    createdBy: userId,
    status: true,
    country: null,
  });

  // Validation states
  const [groupFormErrors, setGroupFormErrors] = useState({});

  // Country states
  const [countries, setCountries] = useState([]);
  const [loadingCountries, setLoadingCountries] = useState(false);

  // Accounts states - UPDATED: Using new API
  const [accounts, setAccounts] = useState([]);
  const [loadingAccounts, setLoadingAccounts] = useState(false);
  const [totalAccounts, setTotalAccounts] = useState(0);

  // Form submission states
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [submitSuccess, setSubmitSuccess] = useState("");

  // UPDATED: Add Notes field to AddNewGroup component
  const [notes, setNotes] = useState("");

  // Load countries on component mount
  const loadCountries = async () => {
    setLoadingCountries(true);
    try {
      const result = await fetchCountries();

      if (result.success) {
        setCountries(result.data);
      } else {
        console.error("Failed to fetch countries:", result.message);
      }
    } catch (error) {
      console.error("Error loading countries:", error);
    } finally {
      setLoadingCountries(false);
    }
  };

  // UPDATED: Load accounts from new dropdown API
  const loadAccounts = async () => {
    setLoadingAccounts(true);
    try {
      const result = await loadAccountDropdown();

      if (result.success) {
        setAccounts(result.data);
        setTotalAccounts(result.totalAccounts);
        console.log("Successfully loaded account dropdown:", {
          accounts: result.data,
          totalAccounts: result.totalAccounts,
          message: result.message,
        });
      } else {
        console.error("Failed to fetch account dropdown:", result.message);
        setAccounts([]);
        setTotalAccounts(0);
      }
    } catch (error) {
      console.error("Error loading account dropdown:", error);
      setAccounts([]);
      setTotalAccounts(0);
    } finally {
      setLoadingAccounts(false);
    }
  };

  useEffect(() => {
    if (open) {
      loadCountries();
      loadAccounts(); // Load accounts when dialog opens
      // Reset submission states when dialog opens
      setSubmitError("");
      setSubmitSuccess("");
    }
  }, [open]);

  // Reset or populate form when dialog opens/closes - UPDATED
  useEffect(() => {
    if (open) {
      if (editingGroup) {
        console.log("Editing group data received:", editingGroup);

        // UPDATED: Properly map accountIds to account objects for editing using new API data
        const findAccountsByIds = (accountIds) => {
          if (!Array.isArray(accountIds) || accountIds.length === 0) {
            console.log("No accountIds found for editing group");
            return [];
          }

          return accountIds
            .map((accountId) => {
              // Try to find the account by ID in the loaded accounts from new API
              const foundAccount = accounts.find(
                (acc) => acc.accountId === accountId
              );
              if (foundAccount) {
                return foundAccount;
              }
              // If not found, create a placeholder object with the ID
              console.warn(
                `Account with ID ${accountId} not found in loaded accounts`
              );
              return {
                accountName: `Account ${accountId}`,
                accountId: accountId,
              };
            })
            .filter((account) => account !== null);
        };

        setGroupForm({
          groupName: editingGroup.groupName || "",
          description: editingGroup.description || "",
          groupType: editingGroup.groupType || "",
          accounts: findAccountsByIds(editingGroup.accountIds || []), // Use accountIds from editing data
          createdBy: editingGroup.createdBy || userId,
          status: editingGroup.status === "Active",
          country: editingGroup.country || null,
        });

        // UPDATED: Set notes for editing
        setNotes(editingGroup.notes || "");

        console.log(
          "Set form accounts:",
          findAccountsByIds(editingGroup.accountIds || [])
        );
      } else {
        // Reset form for new group
        resetGroupForm();
      }
      setGroupFormErrors({});
    }
  }, [open, editingGroup, accounts]); // Added accounts dependency

  // Validation function - Make accountIds mandatory for edit
  const validateGroupForm = () => {
    const errors = {};

    if (!groupForm.groupName.trim())
      errors.groupName = "Group name is required";
    if (!groupForm.groupType.trim())
      errors.groupType = "Group type is required";

    // REMOVED: createdBy validation — auto-populated from token

    // REMOVED: Account validation for editing - now optional for both create and edit

    if (!groupForm.country) errors.country = "Country is required";

    setGroupFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Handle form submission with new API and proper form data mapping
  const handleSubmit = async () => {
    // Clear previous messages
    setSubmitError("");
    setSubmitSuccess("");

    // UPDATED: Validation - removed accountIds requirement
    if (!groupForm.groupName.trim()) {
      setSubmitError("Group name is required");
      return;
    }

    if (!groupForm.groupType) {
      setSubmitError("Group type is required");
      return;
    }

    setSubmitting(true);

    try {
      // FIXED: Check isCloning flag - if cloning, treat as new group creation
      if (editingGroup && !isCloning) {
        // Only use edit flow if NOT cloning
        // For editing, prepare data with proper accountIds and editMode
        const groupData = {
          ...groupForm,
          status: groupForm.status ? "Active" : "Inactive",
          groupId: editingGroup.groupId, // IMPORTANT: Preserve original groupId
          accountIds: groupForm.accounts.map((account) => account.accountId), // Use accountIds directly
          accounts: groupForm.accounts.map((account) => account.accountName),
          accountsCount: groupForm.accounts.length,
          id: editingGroup.id,
          entityType: editingGroup.entityType || "Account", // Preserve entity type
          editMode: "EditGroup", // Backend identifier for edit operation
          notes: notes, // ADDED: Include notes
        };

        console.log("Submitting group edit with data:", groupData);

        onSubmit(groupData, "edit");
        handleClose();
      } else {
        // Create new group OR clone group using POST API with proper form data mapping
        const apiData = {
          entityType: "Account",
          groupName: groupForm.groupName, // From UI form
          description: groupForm.description, // From UI form
          notes: notes, // ADDED: Include notes
          groupType: groupForm.groupType, // From UI form
          country: groupForm.country
            ? `${groupForm.country.countryName} (${groupForm.country.countryCode})`
            : "", // From UI form - formatted country
          status: groupForm.status ? "Active" : "Inactive", // From UI form - converted to string
          accountIds: groupForm.accounts.map((account) => account.accountId), // From UI form - selected account IDs from new API
        };

        console.log(
          isCloning
            ? "Cloning group - creating new group with POST API:"
            : "Submitting new group data with form mapping:",
          apiData
        );

        // Use createNewGroup API (POST request)
        const result = await createNewGroup(apiData);

        if (result.success) {
          setSubmitSuccess(
            isCloning
              ? `Group cloned successfully as "${groupForm.groupName}"`
              : result.message
          );

          // Prepare data for parent component
          const groupData = {
            ...groupForm,
            status: groupForm.status ? "Active" : "Inactive",
            accountsCount: groupForm.accounts.length,
            accounts: groupForm.accounts.map((account) => account.accountName),
            accountIds: groupForm.accounts.map((account) => account.accountId),
            notes: notes, // ADDED: Include notes
            id: result.data?.groupId || result.data?.id || Date.now(), // Use API response ID or fallback
          };

          console.log(
            isCloning
              ? "Group cloned successfully, prepared data for parent:"
              : "Group creation successful, prepared data for parent:",
            groupData
          );

          // Notify parent component
          onSubmit(groupData, "add");

          // Close dialog after a short delay to show success message
          setTimeout(() => {
            handleClose();
          }, 1500);
        } else {
          setSubmitError(result.message);
        }
      }
    } catch (error) {
      console.error("Error submitting group:", error);
      setSubmitError("An unexpected error occurred. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  // Reset form
  const resetGroupForm = () => {
    setGroupForm({
      groupName: "",
      description: "",
      groupType: "",
      accounts: [],
      createdBy: userId,
      status: true,
      country: null,
    });
    setGroupFormErrors({});
    setSubmitError("");
    setSubmitSuccess("");
    setNotes(""); // ADDED: Reset notes
  };

  // Handle dialog close
  const handleClose = () => {
    onClose();
    setGroupFormErrors({});
    setSubmitError("");
    setSubmitSuccess("");
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{ sx: { borderRadius: 3, maxHeight: "90vh" } }}
    >
      <DialogTitle
        sx={{
          fontWeight: 600,
          fontSize: "1.25rem",
          color: "#4B5AB6",
          textAlign: "center",
          pb: 1,
        }}
      >
        {isCloning
          ? "Clone Group"
          : editingGroup
          ? "Edit Group"
          : "Add New Group"}
      </DialogTitle>

      <DialogContent dividers sx={{ p: 3 }}>
        {/* UPDATED: Updated description */}
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ mb: 3, textAlign: "center" }}
        >
          {editingGroup
            ? "Update the group details and modify account associations as needed. Account selection is optional."
            : "Create a new group to organize your accounts. You can add accounts now or later as needed."}
        </Typography>

        {/* Success/Error Messages */}
        {submitError && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {submitError}
          </Alert>
        )}
        {submitSuccess && (
          <Alert severity="success" sx={{ mb: 3 }}>
            {submitSuccess}
          </Alert>
        )}

        {/* Basic Group Information */}
        <Typography
          variant="h6"
          sx={{
            fontWeight: 600,
            mb: 3,
            color: "#1e293b",
            borderBottom: "2px solid #e2e8f0",
            pb: 1,
          }}
        >
          Basic Group Information
        </Typography>

        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              required
              label="Group Name"
              value={groupForm.groupName}
              onChange={(e) => {
                setGroupForm({ ...groupForm, groupName: e.target.value });
                if (groupFormErrors.groupName) {
                  setGroupFormErrors({ ...groupFormErrors, groupName: "" });
                }
              }}
              error={!!groupFormErrors.groupName}
              helperText={
                groupFormErrors.groupName ||
                "Enter a descriptive name for the group"
              }
              disabled={submitting}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <FormControl
              fullWidth
              error={!!groupFormErrors.groupType}
              required
              disabled={submitting}
            >
              <InputLabel>Group Type</InputLabel>
              <Select
                value={groupForm.groupType}
                label="Group Type"
                onChange={(e) => {
                  setGroupForm({ ...groupForm, groupType: e.target.value });
                  if (groupFormErrors.groupType) {
                    setGroupFormErrors({ ...groupFormErrors, groupType: "" });
                  }
                }}
              >
                {groupTypes.map((type) => (
                  <MenuItem key={type} value={type}>
                    {type}
                  </MenuItem>
                ))}
              </Select>
              {groupFormErrors.groupType && (
                <Typography
                  variant="caption"
                  color="error"
                  sx={{ mt: 0.5, ml: 2, display: "block" }}
                >
                  {groupFormErrors.groupType}
                </Typography>
              )}
            </FormControl>
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Group Description"
              multiline
              rows={3}
              value={groupForm.description}
              onChange={(e) =>
                setGroupForm({ ...groupForm, description: e.target.value })
              }
              disabled={submitting}
              helperText="Optional: Describe the purpose of this group"
            />
          </Grid>

          {/* Notes Field */}
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Notes"
              placeholder="Add any additional notes or information about this group..."
              multiline
              rows={4}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              InputProps={{
                startAdornment: (
                  <Box
                    sx={{
                      mr: 1,
                      display: "flex",
                      alignItems: "flex-start",
                      pt: 1,
                    }}
                  >
                    📝
                  </Box>
                ),
              }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: 2,
                },
              }}
              helperText="Optional: Add notes, usage instructions, or any other relevant information about this group"
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <Autocomplete
              options={countries}
              getOptionLabel={(option) =>
                `${option.countryName} (${option.countryCode})`
              }
              value={groupForm.country}
              loading={loadingCountries}
              disabled={submitting}
              onChange={(event, newValue) => {
                setGroupForm({ ...groupForm, country: newValue });
                if (groupFormErrors.country) {
                  setGroupFormErrors({ ...groupFormErrors, country: "" });
                }
              }}
              renderOption={(props, option) => (
                <Box component="li" {...props} key={option.countryCode}>
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {option.countryName}
                    </Typography>
                    <Typography variant="caption" sx={{ color: "#64748b" }}>
                      {option.countryCode} • {option.zoneName} •{" "}
                      {option.regionName}, {option.territoryName}
                    </Typography>
                  </Box>
                </Box>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Country"
                  required
                  error={!!groupFormErrors.country}
                  helperText={
                    groupFormErrors.country ||
                    "Select the country for this group"
                  }
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {loadingCountries ? (
                          <CircularProgress color="inherit" size={20} />
                        ) : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
              noOptionsText="Loading countries..."
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Created By"
              value={groupForm.createdBy}
              disabled
              InputProps={{ readOnly: true }}
              helperText="Auto-filled from your account"
              sx={{
                '& .MuiInputBase-input.Mui-disabled': {
                  WebkitTextFillColor: '#1e293b',
                  color: '#1e293b',
                },
              }}
            />
          </Grid>

          <Grid item xs={12}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                height: "100%",
                pt: 1,
              }}
            >
              <FormControlLabel
                control={
                  <Switch
                    checked={groupForm.status}
                    onChange={(e) =>
                      setGroupForm({ ...groupForm, status: e.target.checked })
                    }
                    disabled={submitting}
                  />
                }
                label="Active Status"
                sx={{
                  "& .MuiFormControlLabel-label": {
                    fontWeight: 500,
                    color: groupForm.status ? "#15803d" : "#64748b",
                  },
                }}
              />
            </Box>
          </Grid>
        </Grid>

        {/* Account Selection Section */}
        <Typography
          variant="h6"
          sx={{
            fontWeight: 600,
            mb: 3,
            color: "#1e293b",
            borderBottom: "2px solid #e2e8f0",
            pb: 1,
          }}
        >
          Account Selection (Optional){" "}
          {/* UPDATED: Added (Optional) to header */}
        </Typography>

        <Grid container spacing={3}>
          <Grid item xs={12}>
            {/* UPDATED: Accounts Autocomplete - removed required, made optional */}
            <Autocomplete
              multiple
              options={accounts}
              getOptionLabel={(option) => option.accountName || ""}
              value={groupForm.accounts}
              loading={loadingAccounts}
              disabled={submitting}
              onChange={(event, newValue) => {
                setGroupForm({ ...groupForm, accounts: newValue });
                if (groupFormErrors.accounts) {
                  setGroupFormErrors({ ...groupFormErrors, accounts: "" });
                }
                console.log("Selected accounts from new API:", newValue);
              }}
              renderOption={(props, option) => (
                <Box component="li" {...props} key={option.accountId}>
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      width: "100%",
                    }}
                  >
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {option.accountName}
                    </Typography>
                    <Typography variant="caption" sx={{ color: "#64748b" }}>
                      ID: {option.accountId}
                    </Typography>
                  </Box>
                </Box>
              )}
              renderTags={(tagValue, getTagProps) =>
                tagValue.map((option, index) => (
                  <Box
                    key={option.accountId}
                    sx={{
                      display: "inline-flex",
                      alignItems: "center",
                      bgcolor: "#e3f2fd",
                      borderRadius: 1,
                      px: 1.5,
                      py: 0.5,
                      m: 0.5,
                      fontSize: "0.875rem",
                      maxWidth: "200px",
                    }}
                    {...getTagProps({ index })}
                  >
                    <Typography
                      variant="caption"
                      sx={{
                        color: "#1976d2",
                        fontWeight: 500,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {option.accountName}
                    </Typography>
                  </Box>
                ))
              }
              isOptionEqualToValue={(option, value) => {
                if (!option || !value) return false;
                return option.accountId === value.accountId;
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Select Accounts (Optional)" // UPDATED: Clear optional label
                  required={false} // UPDATED: Explicitly set to false
                  error={!!groupFormErrors.accounts}
                  helperText={
                    groupFormErrors.accounts ||
                    `${totalAccounts} accounts available • ${groupForm.accounts.length} selected • Optional: You can add accounts later if needed`
                  } // UPDATED: Clear optional messaging
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {loadingAccounts ? (
                          <CircularProgress
                            color="inherit"
                            size={20}
                            sx={{ mr: 1 }}
                          />
                        ) : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
              noOptionsText={
                loadingAccounts
                  ? "Loading accounts..."
                  : accounts.length === 0
                  ? "No accounts found. Please check your connection."
                  : "No matching accounts"
              }
            />
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions
        sx={{
          p: 3,
          pt: 2,
          justifyContent: "space-between",
          bgcolor: "#f8fafc",
        }}
      >
        <Button
          onClick={resetGroupForm}
          variant="outlined"
          disabled={submitting}
          sx={{
            color: "#64748b",
            borderColor: "#e2e8f0",
            "&:hover": {
              borderColor: "#cbd5e1",
              bgcolor: "#f1f5f9",
            },
          }}
        >
          Reset Form
        </Button>
        <Box sx={{ display: "flex", gap: 2 }}>
          <Button
            onClick={handleClose}
            disabled={submitting}
            sx={{ color: "#64748b" }}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            onClick={handleSubmit}
            variant="contained"
            disabled={loadingAccounts || loadingCountries || submitting}
            sx={{
              bgcolor: "#4B5AB6",
              "&:hover": { bgcolor: "#3a4891" },
              borderRadius: 2,
              px: 4,
            }}
          >
            {submitting ? (
              <CircularProgress size={24} />
            ) : isCloning ? (
              "Clone Group"
            ) : editingGroup ? (
              "Update Group"
            ) : (
              "Add Group"
            )}
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default AddNewGroup;
